import React from "react"

import { Button, Progress } from "@coval/ui"
import { Music, X } from "lucide-react"

import MiniAudioPlayer from "@/components/audio-player/MiniAudioPlayer"

import TranscriptEditor from "./TranscriptEditor"
import type { AudioFile } from "./types"

interface AudioFileRowProps {
  audioFile: AudioFile
  isTranscriptExpanded: boolean
  onRemove: () => void
  onToggleTranscript: () => void
  onTranscriptChange: (text: string) => void
  onError: (message: string | null) => void
}

const AudioFileRow: React.FC<AudioFileRowProps> = ({
  audioFile,
  isTranscriptExpanded,
  onRemove,
  onToggleTranscript,
  onTranscriptChange,
  onError,
}) => (
  <div className="bg-surface-0 rounded-lg border border-coval-border">
    <div className="flex items-center gap-3 p-2">
      <div className="shrink-0">
        {audioFile.previewUrl && audioFile.status === "done" ? (
          <MiniAudioPlayer
            audioUrl={audioFile.previewUrl}
            name={audioFile.file.name}
          />
        ) : (
          <div className="w-8 h-8 flex items-center justify-center">
            <Music className="w-4 h-4 text-coval-text-muted" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-coval-text-muted truncate">
          {audioFile.file.name}
        </p>
        <p className="text-xs text-coval-text-muted">
          {(audioFile.file.size / 1024 / 1024).toFixed(1)} MB
        </p>
        {audioFile.status === "uploading" && (
          <Progress
            value={audioFile.progress}
            className="mt-1"
            aria-label={`Uploading ${audioFile.file.name}`}
          />
        )}
        {audioFile.status === "error" && (
          <p className="text-xs text-coval-danger-text mt-1">
            {audioFile.error || "Upload failed"}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {audioFile.status === "done" && (
          <span className="text-xs text-coval-success-text font-medium">
            Uploaded
          </span>
        )}
        {audioFile.status === "uploading" && (
          <span className="text-xs text-coval-contrast font-medium">
            Uploading...
          </span>
        )}
        <Button
          icon
          variant="ghost"
          onClick={onRemove}
          tooltip={`Remove ${audioFile.file.name}`}
        >
          <X className="w-4 h-4 text-coval-text-muted" />
        </Button>
      </div>
    </div>

    {audioFile.status === "done" && (
      <TranscriptEditor
        transcript={audioFile.groundTruthTranscript}
        isExpanded={isTranscriptExpanded}
        onToggle={onToggleTranscript}
        onTranscriptChange={onTranscriptChange}
        onError={onError}
      />
    )}
  </div>
)

export default AudioFileRow
